import { useCallback, useEffect, useMemo, useState } from "react";
import { StartSessionStep } from "./startSessionStep";
import { InitialRestitutionReviewStep } from "./initialRestitutionReviewStep";
import { RestitutionCorrectionStep } from "./restitutionCorrectionStep";
import {
  RestitutionValidationAction,
  RestitutionValidationStep,
} from "./restitutionValidationStep";
import { CreateInitialRestitutionForLastSessionStep } from "./createInitialRestitutionForLastSessionStep";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { ErrorPage } from "../../components/ErrorPage";
import { menuSlice } from "../../qrCode/menuSlice";
import { selectLastSession } from "../../qrCode/driverKnownSlice";
import { selectLatestTelemetryMileage } from "../../qrCode/vehicleLoginSlice";
import {
  getInitialVehicleRestitutionBySessionAsync,
  getVehicleRestitutionPicturesAsync,
  selectCreateCorrectiveVehicleRestitutionStatus,
  selectCreateInitialVehicleRestitutionStatus,
  selectGetInitialVehicleRestitutionBySessionStatus,
  selectGetVehicleRestitutionPicturesStatus,
  selectInitialVehicleRestitution,
  selectValidateVehicleRestitutionStatus,
  selectVehicleRestitutionPictures,
  vehicleSessionsSlice,
} from "../vehicleRestitution/vehicleSessionsSlice";

type BeforeDepartureStep =
  | "startSession"
  | "initialRestitutionCheck"
  | "createInitialRestitution"
  | "correction"
  | "validation";

export const VehicleBeforeDeparturePage = () => {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<BeforeDepartureStep>("startSession");

  const lastSession = useAppSelector(selectLastSession);
  const getInitialRestitutionStatus = useAppSelector(
    selectGetInitialVehicleRestitutionBySessionStatus,
  );
  const getRestitutionPictureStatus = useAppSelector(
    selectGetVehicleRestitutionPicturesStatus,
  );
  const initialRestitution = useAppSelector(selectInitialVehicleRestitution);
  const restitutionPictures = useAppSelector(selectVehicleRestitutionPictures);
  const validateStatus = useAppSelector(selectValidateVehicleRestitutionStatus);
  const createCorrectiveStatus = useAppSelector(
    selectCreateCorrectiveVehicleRestitutionStatus,
  );
  const createInitialStatus = useAppSelector(
    selectCreateInitialVehicleRestitutionStatus,
  );

  const latestTelemetryMileage = useAppSelector(selectLatestTelemetryMileage);

  const [mileage, setMileage] = useState(0);
  const [isCabinCleaned, setIsCabinCleaned] = useState(false);
  const [isTrunkCleaned, setIsTrunkCleaned] = useState(false);
  const [observations, setObservations] = useState("");
  const [oldPictures, setOldPictures] = useState<
    { uuid: string; url: string }[]
  >([]);
  const [newPictures, setNewPictures] = useState<File[]>([]);
  const [validationAction, setValidationAction] =
    useState<RestitutionValidationAction>("initial");

  useEffect(() => {
    if (lastSession && getInitialRestitutionStatus === "idle") {
      void dispatch(
        getInitialVehicleRestitutionBySessionAsync({
          driversHistoryUuid: lastSession.uuid,
        }),
      );
    }
  }, [dispatch, lastSession, getInitialRestitutionStatus]);

  useEffect(() => {
    if (
      getInitialRestitutionStatus === "success" &&
      initialRestitution &&
      getRestitutionPictureStatus === "idle"
    ) {
      void dispatch(
        getVehicleRestitutionPicturesAsync({
          restitution: initialRestitution.uuid,
        }),
      );
    }
  }, [
    dispatch,
    getInitialRestitutionStatus,
    initialRestitution,
    getRestitutionPictureStatus,
  ]);

  useEffect(() => {
    if (getInitialRestitutionStatus === "success" && initialRestitution) {
      setMileage(initialRestitution.mileage);
      setIsCabinCleaned(initialRestitution.isCabinCleaned);
      setIsTrunkCleaned(initialRestitution.isTrunkCleaned);
      setObservations(initialRestitution.observations ?? "");
    }
  }, [getInitialRestitutionStatus, initialRestitution]);

  useEffect(() => {
    if (getRestitutionPictureStatus === "success" && restitutionPictures) {
      setOldPictures(restitutionPictures);
    }
  }, [getRestitutionPictureStatus, restitutionPictures]);

  useEffect(() => {
    if (validateStatus === "success") {
      setValidationAction("validated");
      setStep("validation");
      dispatch(
        vehicleSessionsSlice.actions.resetValidateVehicleRestitutionStatus(),
      );
    }
  }, [validateStatus, dispatch]);

  useEffect(() => {
    if (createCorrectiveStatus === "success") {
      setValidationAction("corrected");
      setStep("validation");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateCorrectiveVehicleRestitutionStatus(),
      );
    }
  }, [createCorrectiveStatus, dispatch]);

  useEffect(() => {
    if (createInitialStatus === "success") {
      setValidationAction("initial");
      setStep("validation");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateInitialVehicleRestitutionStatus(),
      );
    }
  }, [createInitialStatus, dispatch]);

  const onSubmitStartSession = useCallback(
    (date: Date) => {
      const start = new Date(date);
      start.setSeconds(0, 0);
      dispatch(menuSlice.actions.setSessionStartOfUsage(start.toISOString()));
      setStep(
        initialRestitution
          ? "initialRestitutionCheck"
          : "createInitialRestitution",
      );
    },
    [dispatch, initialRestitution],
  );

  const onCorrect = useCallback(() => {
    setStep("correction");
  }, []);

  const hasTelemetryMileage = useMemo(
    () => latestTelemetryMileage !== null && latestTelemetryMileage > 0,
    [latestTelemetryMileage],
  );
  const displayedMileage = useMemo(
    () => (hasTelemetryMileage ? Number(latestTelemetryMileage) : mileage),
    [hasTelemetryMileage, latestTelemetryMileage, mileage],
  );

  // the page is only reachable through the QR code flow, which loads the last session
  if (!lastSession) return <ErrorPage page="qrCode" />;

  switch (step) {
    case "startSession":
      return <StartSessionStep onSubmit={onSubmitStartSession} />;
    case "initialRestitutionCheck":
      return <InitialRestitutionReviewStep onCorrect={onCorrect} />;
    case "createInitialRestitution":
      return (
        <CreateInitialRestitutionForLastSessionStep
          mileage={displayedMileage}
          setMileage={setMileage}
          hasTelemetryMileage={hasTelemetryMileage}
          isCabinCleaned={isCabinCleaned}
          setIsCabinCleaned={setIsCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          setIsTrunkCleaned={setIsTrunkCleaned}
          observations={observations}
          setObservations={setObservations}
          photos={newPictures}
          setPhotos={setNewPictures}
          onReturn={() => setStep("startSession")}
        />
      );
    case "correction":
      return (
        <RestitutionCorrectionStep
          mileage={String(displayedMileage)}
          setMileage={(value) => setMileage(value === "" ? 0 : Number(value))}
          hasTelemetryMileage={hasTelemetryMileage}
          isCabinCleaned={isCabinCleaned}
          setIsCabinCleaned={setIsCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          setIsTrunkCleaned={setIsTrunkCleaned}
          observations={observations}
          setObservations={setObservations}
          uploadedPictures={oldPictures}
          setOldPictures={setOldPictures}
          newPhotos={newPictures}
          setNewPhotos={setNewPictures}
          onReturn={() => setStep("initialRestitutionCheck")}
        />
      );
    case "validation":
      return (
        <RestitutionValidationStep
          action={validationAction}
          mileage={displayedMileage}
          isCabinCleaned={isCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          observations={observations}
          oldPictures={oldPictures}
          newPictures={newPictures}
        />
      );
  }
};
